/**
 * Pure helpers for the dashboard "Your submissions need you" section.
 *
 * A ticket needs the submitter's attention when:
 *  - 'Ready for Testing'     — a fix is up; the submitter should verify it, or
 *  - 'Waiting on Submitter'  — the admin/AI asked a question; work is parked
 *                              until the submitter replies.
 *
 * Kept free of React/MUI so the filtering + ordering + messaging logic is
 * unit-testable without rendering.
 */
import type { FeedbackTicket, FeedbackTicketStatus } from '../../types/feedback';

/** Statuses that put a ticket in the submitter's court. */
export const ATTENTION_STATUSES: readonly FeedbackTicketStatus[] = [
  'Ready for Testing',
  'Waiting on Submitter',
];

/** Human message shown next to the status chip, one per attention status. */
export const ATTENTION_MESSAGES: Record<string, string> = {
  'Ready for Testing': 'ready for you to verify',
  'Waiting on Submitter': 'we asked you a question — please reply',
};

export function attentionMessage(status: string): string {
  return ATTENTION_MESSAGES[status] || '';
}

/**
 * Filter the user's tickets down to the ones needing their attention.
 * Order: 'Waiting on Submitter' first (those block work on our side),
 * then 'Ready for Testing'; newest first within each group.
 */
export function filterAttentionTickets(tickets: FeedbackTicket[]): FeedbackTicket[] {
  const rank = (t: FeedbackTicket) => (t.Status === 'Waiting on Submitter' ? 0 : 1);
  return tickets
    .filter((t) => (ATTENTION_STATUSES as readonly string[]).includes(t.Status))
    .sort((a, b) => {
      const byRank = rank(a) - rank(b);
      if (byRank !== 0) return byRank;
      const aT = new Date(a.sysCreateDate).getTime() || 0;
      const bT = new Date(b.sysCreateDate).getTime() || 0;
      return bT - aT;
    });
}
